import React, { useEffect, useState } from 'react'
import { freshService } from '../services/freshService'
import { utilService } from '../services/utilService'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons'
const { Dropdown } = require('monday-ui-react-core')
const sourceTooltip = require('../assets/images/source.jpg')

interface FreshFieldsProps {
  isEditable: boolean
  mappingTicketFields: TicketFields
  onSetMapping: Function
  freshAccount: FreshAccount
  boardColumns: DropDownOption[]
  setUnfilledRequiredFresh: React.Dispatch<React.SetStateAction<string[]>>
  ticketFieldsData: any[]
  setTicketFieldsData: React.Dispatch<React.SetStateAction<any>>
  initialAgentOptions: any[] | undefined
  setInitialAgentOptions: React.Dispatch<React.SetStateAction<any>>
}

export const FreshFields: React.FC<FreshFieldsProps> = ({ isEditable, mappingTicketFields, onSetMapping, freshAccount, boardColumns, setUnfilledRequiredFresh, ticketFieldsData, setTicketFieldsData, initialAgentOptions, setInitialAgentOptions }) => {
  const [ticketFields, setTicketFields] = useState<{ [key: string]: null | DropDownOption }>(utilService.getTicketFields(mappingTicketFields, ticketFieldsData))
  const [ticketAgentIdx, setTicketAgentIdx] = useState<number>()
  const [mondaySource, setMondaySource] = useState<any>()
  const [isTooltipOn, setIsTooltipOn] = useState<boolean>(false)

  useEffect(() => {    
    onSetMapping('ticketFields', ticketFields)
  }, [])
  /**
   * The useEffect splitted to two because of infinite loop when using the "ticketFieldData" as dependency if we call "setAgentsByGroup"
   */

  useEffect(() => {
    const ticketAgentDataIdx = ticketFieldsData?.findIndex((field: TicketFieldsData) => field.name === 'agent')
    setTicketAgentIdx(ticketAgentDataIdx)

    if (ticketFieldsData) {
      getUnfilledRequiredFresh(ticketFieldsData)
      checkIfMondaySourceExist()
    }
  }, [ticketFieldsData])

  /**
   * The function checks the unfilled mendatory fields and dispatch it to the "Mapping" component to check if all required fields are filled while saving.
   */
  const getUnfilledRequiredFresh = (ticketFieldsData: TicketFieldsData[]) => {
    const unfilledRequiredFresh: string[] = []
    ticketFieldsData.forEach((ticketField: TicketFieldsData) => {
      if (!ticketFields[utilService.convertFreshFieldName(ticketField.name) as string] && ticketField.isRequired) {
        // if((!ticketFieldKeys.includes(utilService.convertFreshFieldName(ticketField.name)) && ticketField.isRequired)) {
        unfilledRequiredFresh.push(ticketField.label)
      }
    })
    setUnfilledRequiredFresh(unfilledRequiredFresh)
  }

  useEffect(() => {
    setAgentsByGroup()
  }, [ticketAgentIdx, ticketFields, initialAgentOptions])

   /**
   * RESETING WHEN CLEARING
   */
  useEffect(() => {
    setTicketFields(utilService.getTicketFields(mappingTicketFields, ticketFieldsData))
  }, [mappingTicketFields])

  /**
   * In case that the user chose a group, only specific agents can be the responder.
   * The function filtering the agents from the initial agents in the state.
   */
  const setAgentsByGroup = async (): Promise<void> => {
    if (!initialAgentOptions || !ticketAgentIdx || !ticketFields) return
    const copyTicketFieldsData: TicketFieldsData[] = [...ticketFieldsData!]
    if (ticketFields.group_id) {
      const agentsByGroup: number[] = await freshService.getAgentsGroups(freshAccount, ticketFields.group_id.value)
      const filteredAgents = initialAgentOptions?.filter((agent: FreshRes) => agentsByGroup.indexOf(agent.id) !== -1)
      if (process.env.NODE_ENV !== 'production') console.log('filteredAgents', filteredAgents)
      copyTicketFieldsData![ticketAgentIdx!].options = filteredAgents
      setTicketFieldsData(copyTicketFieldsData)
    } else {
      copyTicketFieldsData![ticketAgentIdx!].options = initialAgentOptions
      setTicketFieldsData(copyTicketFieldsData)
    }
  }

  const onSetTicketFields = (option: DropDownOption, field: string): void => {
    setTicketFields((ticketFields) => ({
      ...ticketFields,
      [field]: option,
    }))
    ticketFields[field as keyof TicketFields] = option
    onSetMapping('ticketFields', ticketFields)
  }

  const ticketFieldsOptions = (field: TicketFieldsData) => {
    if (!boardColumns?.length) return
    if (field.options.length || field.name === 'agent' || field.name === 'group') {
      return utilService.convertToDropDownOptions(field.options)
    } else {
      const mappedBoardColumns = boardColumns.map((column: any) => {
        return { ...column, fieldType: field.fieldType }
      })
      if (field.fieldType === 'custom_date') return mappedBoardColumns.filter((column) => column.type === 'date') // if custom field is date, column type must be date too
      else if (field.fieldType === 'custom_checkbox') return mappedBoardColumns.filter((column) => column.type === 'boolean') // if custom field is date, column type must be date too
      else if (field.fieldType === 'custom_number' || field.fieldType === 'custom_decimal') return mappedBoardColumns.filter((column) => column.type === 'numeric') // if custom field is number, column type must be number too
      else if (field.fieldType === 'custom_url') return mappedBoardColumns.filter((column) => column.type === 'link') // if custom field is number, column type must be number too
      return mappedBoardColumns
    }
  }

  const checkIfMondaySourceExist = (): void => {
    const source: TicketFieldsData | undefined = ticketFieldsData?.find((ticketFields: TicketFieldsData) => ticketFields.name === 'source')
    const mondaySource: TicketFieldsDataOptions | undefined = source?.options.find((source: TicketFieldsDataOptions) => source.value === 'monday.com')
    setMondaySource(mondaySource)
  }

  const onCreateSource = async (): Promise<void> => {
    const mondaySource = await freshService.createSource(freshAccount, 'monday.com')
    setMondaySource(mondaySource)
    setTicketFields((ticketFields: any) => ({
      ...ticketFields,
      source: mondaySource,
    }))
    ticketFields['source' as keyof TicketFields] = mondaySource
    onSetMapping('ticketFields', ticketFields)
  }

  const GetFreshField: React.FC<{ field: TicketFieldsData }> = ({ field }): any => {
    switch (field.fieldType) {
      case 'custom_date':
        return (
          <Dropdown
            className="dropdown-wrapper"
            disabled={!isEditable}
            placeholder={field.placeholder || 'Choose date column'}
            noOptionsMessage={() => 'Must be date column type'}
            options={ticketFieldsOptions(field)}
            onChange={(option: DropDownOption) => onSetTicketFields(option, utilService.convertFreshFieldName(field.name))}
            value={ticketFields[utilService.convertFreshFieldName(field.name)]}
          />
        )
      case 'custom_number':
        return (
          <Dropdown
            className="dropdown-wrapper"
            disabled={!isEditable}
            placeholder={field.placeholder || 'Choose number column'}
            noOptionsMessage={() => 'Must be number column type'}
            options={ticketFieldsOptions(field)}
            onChange={(option: DropDownOption) => onSetTicketFields(option, utilService.convertFreshFieldName(field.name))}
            value={ticketFields[utilService.convertFreshFieldName(field.name)]}
          />
        )
      case 'custom_decimal':
        return (
          <Dropdown
            className="dropdown-wrapper"
            disabled={!isEditable}
            placeholder={field.placeholder || 'Choose number column'}
            noOptionsMessage={() => 'Must be number column type'}
            options={ticketFieldsOptions(field)}
            onChange={(option: DropDownOption) => onSetTicketFields(option, utilService.convertFreshFieldName(field.name))}
            value={ticketFields[utilService.convertFreshFieldName(field.name)]}
          />
        )
      case 'custom_checkbox':
        return (
          <Dropdown
            className="dropdown-wrapper"
            disabled={!isEditable}
            placeholder={field.placeholder || 'Choose checkbox column'}
            noOptionsMessage={() => 'Must be checkbox column type'}
            options={ticketFieldsOptions(field)}
            onChange={(option: DropDownOption) => onSetTicketFields(option, utilService.convertFreshFieldName(field.name))}
            value={ticketFields[utilService.convertFreshFieldName(field.name)]}
          />
        )
      case 'custom_url':
        return (
          <Dropdown
            className="dropdown-wrapper"
            disabled={!isEditable}
            placeholder={field.placeholder || 'Choose link column'}
            noOptionsMessage={() => 'Must be link column type'}
            options={ticketFieldsOptions(field)}
            onChange={(option: DropDownOption) => onSetTicketFields(option, utilService.convertFreshFieldName(field.name))}
            value={ticketFields[utilService.convertFreshFieldName(field.name)]}
          />
        )
      case 'custom_multi_select_dropdown':
        return '"Multi Dropdown" is not supported.'
      case 'custom_content':
        return field.name
      default:
        return <Dropdown  className="dropdown-wrapper" disabled={!isEditable} placeholder={field.placeholder} options={ticketFieldsOptions(field)} onChange={(option: DropDownOption) => onSetTicketFields(option, utilService.convertFreshFieldName(field.name))} value={ticketFields[utilService.convertFreshFieldName(field.name)]} />
    }
  }

  return (
    <div className="dropdown-container fresh-integration">
      <h2>Ticket Fields</h2>
      {ticketFieldsData.map((field: TicketFieldsData) => (
        <label key={field.id}>
          <p>
            {field.label}: <span>{field.isRequired && '*'}</span>
          </p>
          <GetFreshField field={field} />
          {field.name === 'source' && (
            <div className="tooltip" onMouseLeave={() => setIsTooltipOn(false)}>
              <span onMouseEnter={() => setIsTooltipOn(true)}>
                <FontAwesomeIcon icon={faCircleInfo} />
              </span>
              <div style={isTooltipOn ? { opacity: 1, pointerEvents: 'all' } : { opacity: 0, pointerEvents: 'none' }} className="tooltip-container">
                We recommend adding source field called 'monday.com'
                <img src={sourceTooltip} alt="domain-tooltip" />
                {/* <span onClick={() => setIsSourceEditable(true)} style={{ textDecoration: 'underline', color: 'lightblue', cursor: 'pointer' }}>
                  I want to change source anyways
                </span> */}
                <br />
                {!mondaySource && (
                  <span onClick={() => onCreateSource()} style={{ textDecoration: 'underline', color: 'lightblue', cursor: 'pointer' }}>
                    Create 'monday.com' source
                  </span>
                )}
              </div>
            </div>
          )}
        </label>
      ))}
    </div>
  )
}
